/* ============================================================================
   Recommendation scoring — real logic, not random numbers.
   Every contribution is paired with a plain-language reason so the UI can
   explain the result instead of presenting a black box.
   ========================================================================== */

import type {
  Governorate,
  PlantPurpose,
  SoilType,
  Species,
  SunExposure,
  WaterAvailability,
} from '@core/types'
import { CLIMATE_BY_GOVERNORATE } from '../data/climate'

export const SUN_LABEL: Record<SunExposure, string> = {
  'full-sun': 'full sun',
  'partial-shade': 'partial shade',
  shade: 'shade',
}

const FROST_SENSITIVE = new Set(['sp-bougainvillea', 'sp-lemon', 'sp-washingtonia'])
const FRUIT_SPECIES = new Set(['sp-date-palm', 'sp-lemon', 'sp-white-mulberry', 'sp-sidr'])
const WIND_BREAK_SPECIES = new Set(['sp-athel', 'sp-euphrates-poplar', 'sp-eucalyptus'])
const ORNAMENTAL_SPECIES = new Set([
  'sp-oleander',
  'sp-bougainvillea',
  'sp-washingtonia',
  'sp-conocarpus',
])

export interface SiteConditions {
  governorate: Governorate
  soilType: SoilType
  sunExposure: SunExposure
  waterAvailability: WaterAvailability
  purpose: PlantPurpose
}

export interface ScoredSpecies {
  score: number
  reasons: string[]
  watchOuts: string[]
}

export function scoreSpecies(species: Species, input: SiteConditions): ScoredSpecies {
  const climate = CLIMATE_BY_GOVERNORATE[input.governorate]
  const reasons: string[] = []
  const watchOuts: string[] = []
  let score = 55

  /* soil texture */
  if (species.soilTypes.includes(input.soilType)) {
    score += 15
    reasons.push(`Grows in ${input.soilType} soil, which is what you described.`)
  } else {
    score -= 10
    watchOuts.push(
      `${species.commonName} is not normally planted in ${input.soilType} soil — budget for soil improvement first.`,
    )
  }

  /* salinity — the dominant constraint in central and southern Iraq */
  if (climate.salinityRisk === 'high') {
    if (species.salinityTolerance === 'high') {
      score += 14
      reasons.push(
        `Tolerates saline soil and saline irrigation water, the main constraint in ${input.governorate}.`,
      )
    } else if (species.salinityTolerance === 'medium') {
      score += 4
      reasons.push('Handles moderate salinity as long as you leach the soil each season.')
    } else {
      score -= 16
      watchOuts.push(
        `Low salt tolerance: on the water and soils typical of ${input.governorate}, expect leaf burn and slow growth.`,
      )
    }
  } else if (species.salinityTolerance === 'low') {
    score -= 6
    watchOuts.push('Sensitive to salty irrigation water — test your water source before planting.')
  }

  /* water availability */
  if (input.waterAvailability === 'low') {
    if (species.waterNeed === 'low') {
      score += 10
      reasons.push('Needs little water once established, which suits a limited supply.')
    } else if (species.waterNeed === 'moderate') {
      score -= 6
      watchOuts.push('Needs regular summer watering; with little water available it will struggle.')
    } else {
      score -= 16
      watchOuts.push('Needs reliable irrigation — a poor match if the water supply is uncertain.')
    }
  } else if (input.waterAvailability === 'moderate') {
    if (species.waterNeed === 'moderate') {
      score += 10
      reasons.push('Water requirement matches a moderate supply.')
    } else if (species.waterNeed === 'low') {
      score += 6
      reasons.push('Survives on less water than you have, which gives margin in a dry year.')
    } else {
      score -= 4
      watchOuts.push('Will use more water than a moderate supply at the peak of summer.')
    }
  } else if (species.waterNeed === 'high') {
    score += 6
    reasons.push('Can make full use of a reliable water supply.')
  }

  /* sun exposure */
  if (species.sunExposure === input.sunExposure) {
    score += 8
    reasons.push(`Prefers ${SUN_LABEL[input.sunExposure]}, exactly as you described the site.`)
  } else {
    score -= 6
    watchOuts.push(
      `Prefers ${SUN_LABEL[species.sunExposure]} rather than ${SUN_LABEL[input.sunExposure]}.`,
    )
  }

  /* purpose */
  if (input.purpose === 'fruit' && FRUIT_SPECIES.has(species.id)) {
    score += 12
    reasons.push('Produces an edible harvest, which is what you are planting for.')
  } else if (input.purpose === 'shade' && species.matureHeightM >= 8) {
    score += 10
    reasons.push(`Reaches about ${species.matureHeightM} m, enough to cast useful shade.`)
  } else if (input.purpose === 'windbreak' && WIND_BREAK_SPECIES.has(species.id)) {
    score += 12
    reasons.push('Dense growth and wind tolerance make it a working shelterbelt species.')
  } else if (input.purpose === 'ornamental' && ORNAMENTAL_SPECIES.has(species.id)) {
    score += 12
    reasons.push('Grown for structure and colour rather than yield.')
  } else if (input.purpose === 'medicinal' && species.id === 'sp-sidr') {
    score += 10
    reasons.push('Sidr fruit and leaves are traditionally used, so it matches that purpose.')
  } else if (input.purpose === 'shade') {
    score -= 4
    watchOuts.push(`At about ${species.matureHeightM} m it will not provide much shade.`)
  } else {
    score -= 6
    watchOuts.push('Not the strongest match for the purpose you selected.')
  }

  /* climate zone adaptation */
  if (climate.zone === 'mediterranean') {
    if (FROST_SENSITIVE.has(species.id)) {
      score -= 12
      watchOuts.push(
        `Frost-sensitive: in ${input.governorate} it needs a sheltered, south-facing position or winter protection.`,
      )
    } else {
      score += 4
      reasons.push('Copes with the cooler winters of the northern foothills.')
    }
  }
  if (
    climate.zone === 'marsh' &&
    (species.id === 'sp-oleander' || species.id === 'sp-bougainvillea')
  ) {
    score -= 5
    watchOuts.push('High humidity raises the risk of powdery mildew on this species.')
  }
  if (species.nativeToIraq) {
    score += 5
    reasons.push('Native to Iraq, so it is already adapted to local soils and seasons.')
  }

  /* species-specific honesty */
  if (species.id === 'sp-conocarpus') {
    score -= 10
    watchOuts.push(
      'Fast-growing and invasive, and its pollen is a known allergen. Many municipalities are moving away from it — the native alternatives are a safer long-term choice.',
    )
  }
  if (species.id === 'sp-eucalyptus') {
    score -= 6
    watchOuts.push(
      'Consumes a great deal of water and suppresses undergrowth — keep it away from canals and crop fields.',
    )
  }
  if (species.id === 'sp-oleander') {
    watchOuts.push(
      'All parts are toxic if eaten. Avoid planting where small children play, and never compost the prunings.',
    )
  }
  if (species.difficulty === 'demanding') {
    score -= 4
    watchOuts.push(
      'Needs more attention than the easier species: regular feeding, clean water and frost protection.',
    )
  }

  return {
    score: Math.max(18, Math.min(97, Math.round(score))),
    reasons: reasons.slice(0, 3),
    watchOuts: watchOuts.slice(0, 3),
  }
}
